import React, { useRef, useEffect, useState } from 'react';
import './storeLocator.css';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { LocationList, LocationDetail } from './components';
import {
  returnAndSortLocalisedStores,
  removeStoresPopup,
  undoFlyToStore,
  flyToStore,
  createPopUp,
  initializeMapLayers,
  returnPointOnMapLength,
} from './storeLocator.util';
import { useMediaQuery } from 'utils/hooks/useMediaQuery';
import { formatContentfulComponentProps } from 'utils/globals';
import useStoreLocatorDb from './useStoreLocatorDb';

mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const longitude = 2.3488;
const latitude = 48.8534;
const zoom = 10;
const initialClusteredPointLenght = 20;

export const StoreLocator = ({ list, marker, storeDetails }) => {
  const database = useStoreLocatorDb();

  mapboxgl.accessToken = process.env.GATSBY_MAPBOX_API_KEY;
  // STATE
  const [isLoading, setIsLoading] = useState(true);
  const [currentStore, setCurrentStore] = useState(null);
  const [map, setMap] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(zoom);
  const [activeStoreDetail, setActiveStoreDetail] = useState(false);
  const [activeStoreIndex, setActiveStoreIndex] = useState(null);
  const [lng, setLng] = useState(longitude);
  const [lat, setLat] = useState(latitude);
  const [storeList, setStoreList] = useState(
    returnAndSortLocalisedStores(database, lng, lat)
  );
  const [unClusteredPointLenght, setUnClusteredPointLenght] = useState(
    initialClusteredPointLenght
  );

  // HOOKS
  const mapContainerRef = useRef(null);
  const isWindowSmallSize: boolean = useMediaQuery('sm');

  // When the user clicks on the marker or a vendor on the list
  const handleLocationClick = (store, index) => {
    setActiveStoreDetail(true);
    flyToStore(map, store);
    setActiveStoreIndex(index);
    setCurrentStore(store);
  };

  // INTIALIZE MAP AND GEOCODER
  useEffect(() => {
    if (mapContainerRef) {
      setMap(
        new mapboxgl.Map({
          container: mapContainerRef.current,
          style: process.env.GATSBY_MAPBOX_STYLE_URL,
          center: [lng, lat],
          zoom,
          maxZoom: 18,
          minZoom: 3,
        })
      );

      setGeocoder(
        new MapboxGeocoder({
          accessToken: mapboxgl.accessToken,
          mapboxgl,
          marker: false,
          zoom,
        })
      );
    }
  }, []);

  // PROCESSING STORES
  useEffect(() => {
    if (map && map.getSource('stores-data')) {
      map.getSource('stores-data').setData(storeList);
    }
  }, [storeList]);

  useEffect(() => {
    if (map) {
      // Inital load of controls (zoom in / out)
      map.addControl(
        new mapboxgl.NavigationControl({ showCompass: false }),
        'top-left'
      );

      map.on('load', () => {
        document.getElementById('geocoder').appendChild(geocoder.onAdd(map));

        geocoder.on('result', (e) => {
          // Get the coordinate of the search result
          const searchResult = e.result.geometry;
          const resultLng = parseFloat(searchResult.coordinates[0]);
          const resultLat = parseFloat(searchResult.coordinates[1]);
          setLng(resultLng);
          setLat(resultLat);
        });

        map.addSource('stores-data', {
          type: 'geojson',
          data: storeList,
          cluster: true,
          clusterMaxZoom: 12,
        });

        // Load custom marker on the map
        map.loadImage(`${marker.media.file.url}`, (error, image) => {
          if (error) throw error;
          map.addImage('nooz-marker', image);
        });

        initializeMapLayers(map);

        map.on('click', 'clusters', (e) => {
          // Zoom on the on map when a cluster is cliked
          const features = map.queryRenderedFeatures(e.point, {
            layers: ['clusters'],
          });
          const clusterId = features[0].properties.cluster_id;
          map
            .getSource('stores-data')
            .getClusterExpansionZoom(clusterId, (err, zoom) => {
              if (err) return;

              map.easeTo({
                center: features[0].geometry.coordinates,
                zoom,
              });
            });
        });

        map.on('mouseenter', 'clusters', () => {
          map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', 'clusters', () => {
          map.getCanvas().style.cursor = '';
        });

        map.on('click', 'unclustered-point', (e) => {
          handleLocationClick(e.features[0]);
        });

        map.on('mouseenter', 'unclustered-point', (e) => {
          // trigger location tooltip on desktop only
          // TODO
          if (true) {
            map.getCanvas().style.cursor = 'pointer';
            createPopUp(map, e.features[0]);
          }
        });

        map.on('mouseleave', 'unclustered-point', () => {
          map.getCanvas().style.cursor = '';
          removeStoresPopup();
        });

        setIsLoading(false);
      });

      map.on('moveend', () => {
        const lg = map.getCenter().lng.toFixed(4);
        const lt = map.getCenter().lat.toFixed(4);

        // Set the number of stores visible on the list
        setUnClusteredPointLenght(returnPointOnMapLength(map));
        setCurrentZoom(map.getZoom());
        setStoreList(
          returnAndSortLocalisedStores(database, parseFloat(lg), parseFloat(lt))
        );
      });

      // Get user's position
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          setLng(position.coords.longitude);
          setLat(position.coords.latitude);
        });
      }
    }

    return () => {
      if (map) {
        map.remove();
      }
    };
  }, [map]);

  return (
    <div className='store-locator w-screen flex flex-column lg:flex-row'>
      <div
        className={`map-wrapper absolute z-10 w-full sm:relative sm:border-r ${
          activeStoreDetail ? 'sm:w-1/2' : 'sm:w-1/3'
        } transform transition-all`}
        justifyContent='flex-start'
      >
        <div className='w-full sm:border-b flex justify-center'>
          <div id='geocoder' className='geocoder' />
        </div>
        <div className='hidden sm:block w-full list overflow-scroll no-scrollbar'>
          {!isLoading && activeStoreDetail ? (
            <LocationDetail
              map={map}
              isWindowSmallSize={isWindowSmallSize}
              undoFlyToStore={undoFlyToStore}
              setActiveStoreDetail={setActiveStoreDetail}
              currentStore={currentStore}
              {...formatContentfulComponentProps(storeDetails)}
            />
          ) : (
            !isLoading && (
              <LocationList
                activeStoreIndex={activeStoreIndex}
                createPopUp={createPopUp}
                currentZoom={currentZoom}
                list={storeList.features.slice(0, unClusteredPointLenght)}
                map={map}
                onClick={handleLocationClick}
                removePopup={removeStoresPopup}
                setActiveStoreIndex={setActiveStoreIndex}
                {...formatContentfulComponentProps(list)}
              />
            )
          )}
        </div>
      </div>
      <div
        className='w-full pt-20 sm:pt-4 sm:w-2/3 p-4'
        activeStoreIndex={activeStoreIndex}
      >
        <div className='map-container h-full w-full' ref={mapContainerRef} />
      </div>
    </div>
  );
};
